// src/components/sinister/ModalUpdateResponsible.tsx
import { Modal, Box, Typography, Button, Icon, Paper, Grid, LinearProgress, Autocomplete, TextField, CircularProgress, Chip, Divider } from "@mui/material";
import { VForm } from "../../../../shared/forms";
import { useEffect, useRef, useState, useMemo } from "react";
import { FormHandles } from "@unform/core";
import { userService, IListagemUsuario } from "../../../../shared/services/api/user/userService";
import { debounce } from "@mui/material/utils";
import { globalTranslate } from "../../../../shared/utils";

interface ModalUpdateResponsibleProps {
	open: boolean;
	isLoading: boolean;
	isUploading: boolean;
	handleSubmit: (data: { responsibleId: string }) => void;
	idSinister: string;
	isClose: () => void;
}

export default function ModalUpdateResponsible({ open, isLoading, isUploading, handleSubmit, idSinister, isClose }: ModalUpdateResponsibleProps) {
	const formRef = useRef<FormHandles>(null);
	const [formKey, setFormKey] = useState(0);
	const [selectedUser, setSelectedUser] = useState<IListagemUsuario | null>(null);
	const [options, setOptions] = useState<IListagemUsuario[]>([]);
	const [loadingOptions, setLoadingOptions] = useState(false);

	// Debounced fetch with stable memoized function
	const fetchUsers = useMemo(
		() =>
			debounce(async (query: string) => {
				setLoadingOptions(true);
				const result = await userService.getManagers(query, idSinister);
				if (!(result instanceof Error)) {
					setOptions(result.data);
				}
				setLoadingOptions(false);
			}, 500),
		[idSinister]
	);

	useEffect(() => {
		if (open) {
			setFormKey((prev) => prev + 1);
			setSelectedUser(null);
			formRef.current?.setErrors({});
		}
	}, [open]);

	const internalHandleSubmit = () => {
		if (!selectedUser) {
			formRef.current?.setErrors({ responsibleId: "Selecione um responsável" });
			return;
		}
		handleSubmit({ responsibleId: selectedUser.id });
	};

	const getStatusColor = (status: string) => {
		switch (status?.toLowerCase()) {
			case "active":
				return { bg: "#E0F7FA", color: "#00796B" };
			case "inactive":
				return { bg: "#FCE4EC", color: "#C2185B" };
			default:
				return { bg: "#E0E0E0", color: "#424242" };
		}
	};

	return (
		<Modal open={open} onClose={isClose} disableEnforceFocus>
			<Box
				sx={{
					position: "absolute",
					top: "50%",
					left: "50%",
					transform: "translate(-50%, -50%)",
					width: "45%",
					outline: "none",
				}}
			>
				<Paper sx={{ p: 4 }}>
					<Typography variant="h5" fontWeight={300} mb={2} color="primary" textAlign="center">
						Alteração de responsável do sinistro
					</Typography>

					<VForm
						key={formKey}
						ref={formRef}
						id="formUpdateResponsible"
						onSubmit={internalHandleSubmit}
						onPointerEnterCapture={() => {}}
						onPointerLeaveCapture={() => {}}
						placeholder={"Your placeholder"}
					>
						<Grid container direction="column" spacing={2}>
							{isLoading && (
								<Grid item>
									<LinearProgress />
								</Grid>
							)}

							<Grid item>
								<Typography>Informe o novo responsável pelo sinistro.</Typography>
							</Grid>

							<Grid container item spacing={2}>
								<Grid item xs={12} md={8}>
									<Autocomplete
										options={options}
										value={selectedUser}
										onChange={(_, value) => setSelectedUser(value)}
										onInputChange={(_, value, reason) => {
											if (reason === "input") fetchUsers(value);
										}}
										loading={loadingOptions}
										getOptionLabel={(option) => option.name}
										isOptionEqualToValue={(option, value) => option.id === value.id}
										renderOption={(props, option, { index }) => {
											const { bg, color } = getStatusColor(option.status || "");
											const isLast = index === options.length - 1;

											return (
												<Box
													component="li"
													{...props}
													key={option.id}
													sx={{
														display: "flex",
														flexDirection: "column",
														alignItems: "flex-start",
														justifyContent: "flex-start",
														textAlign: "left",
														px: 1.5,
														py: 1,
														width: "100%",
													}}
												>
													<Box display="flex" alignItems="start" width="100%" gap={1}>
														<Typography fontWeight={500}>{option.name}</Typography>
														{option.status && (
															<Chip
																label={globalTranslate(option.status)}
																size="small"
																sx={{
																	backgroundColor: bg,
																	color,
																	fontSize: "0.75rem",
																	height: "20px",
																}}
															/>
														)}
													</Box>

													<Typography variant="body2" width="100%" alignItems="start" color="text.secondary">
														{option.email}
													</Typography>

													{!isLast && <Divider sx={{ mt: 1, width: "100%" }} />}
												</Box>
											);
										}}
										renderInput={(params) => (
											<TextField
												{...params}
												label="Responsável pelo Sinistro"
												fullWidth
												placeholder="Digite o nome do responsável"
												InputProps={{
													...params.InputProps,
													endAdornment: (
														<>
															{loadingOptions ? <CircularProgress size={20} /> : null}
															{params.InputProps.endAdornment}
														</>
													),
												}}
											/>
										)}
									/>
								</Grid>
							</Grid>

							<Grid container item justifyContent="center" spacing={2} mt={2}>
								<Grid item display="flex" gap={2}>
									<Button variant="contained" color="inherit" startIcon={<Icon>arrow_back</Icon>} onClick={isClose} sx={{ width: "160px" }}>
										Voltar
									</Button>

									<Button
										variant="contained"
										startIcon={<Icon>check</Icon>}
										onClick={() => formRef.current?.submitForm()}
										disabled={isUploading}
										sx={{ width: "160px" }}
									>
										Confirmar
									</Button>
								</Grid>
							</Grid>
						</Grid>
					</VForm>
				</Paper>
			</Box>
		</Modal>
	);
}
